import React, { createContext, useContext, useState, Suspense } from 'react';

const AIAssistantButton = React.lazy(() =>
  import('./AIAssistantButton').then((m) => ({ default: m.AIAssistantButton }))
);

const AIAssistantWindow = React.lazy(() =>
  import('./AIAssistantWindow').then((m) => ({ default: m.AIAssistantWindow }))
);

interface AIAssistantContextType {
  isOpen: boolean;
  openAssistant: () => void;
  closeAssistant: () => void;
  toggleAssistant: () => void;
}

const AIAssistantContext = createContext<AIAssistantContextType | null>(null);

export function useAIAssistant() {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within AIAssistantProvider');
  }
  return context;
}

interface AIAssistantProviderProps {
  children: React.ReactNode;
}

export function AIAssistantProvider({ children }: AIAssistantProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const openAssistant = () => {
    setIsOpen(true);
    setIsMinimized(false);
  };

  const closeAssistant = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const toggleAssistant = () => {
    if (isOpen && !isMinimized) {
      setIsMinimized(true);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
    }
  };

  const minimizeAssistant = () => {
    setIsMinimized(true);
  };

  const restoreAssistant = () => {
    setIsMinimized(false);
  };

  const contextValue: AIAssistantContextType = {
    isOpen,
    openAssistant,
    closeAssistant,
    toggleAssistant
  };

  return (
    <AIAssistantContext.Provider value={contextValue}>
      {children}
      
      {/* Floating AI Assistant Button - positioned next to chat button */}
      <div className="fixed bottom-6 right-24 z-50">
        <Suspense fallback={null}>
          <AIAssistantButton
            onClick={toggleAssistant}
            isOpen={isOpen && !isMinimized}
          />
        </Suspense>
      </div>

      {/* AI Assistant Window */}
      {isOpen && (
        <Suspense fallback={null}>
          <AIAssistantWindow
            isMinimized={isMinimized}
            onClose={closeAssistant}
            onMinimize={minimizeAssistant}
          />
        </Suspense>
      )}
    </AIAssistantContext.Provider>
  );
}
